import React from 'react';
import { useInfluencerStore } from '@/stores/influencerStore';
import { COMMISSION_RATES, CommissionTier } from '@/types/influencer';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export const EarningsCalculator: React.FC = () => {
  const { dashboardData, tier } = useInfluencerStore();

  if (!dashboardData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No earnings data available</p>
      </div>
    );
  }

  const { personalStats } = dashboardData;
  const currentTier = tier.current;
  const nextTier = currentTier < 3 ? currentTier + 1 : null;

  const calculateTierEarnings = (tierLevel: number, signups: number) => {
    const tierData = COMMISSION_RATES[`TIER_${tierLevel}` as keyof typeof COMMISSION_RATES];
    if (!tierData) return 0;
    
    if (signups >= tierData.signups) {
      return tierData.bonus;
    }
    return signups * tierData.rate;
  };

  const currentEarnings = calculateTierEarnings(currentTier, personalStats.totalSignups);
  const nextTierEarnings = nextTier ? calculateTierEarnings(nextTier, personalStats.totalSignups) : 0;
  const potentialEarnings = nextTier ? calculateTierEarnings(nextTier, tier.nextTierRequirement) : currentEarnings;

  const earningsData = [
    { tier: 'Tier 1', signups: 10, earnings: 50, current: currentTier === 1 },
    { tier: 'Tier 2', signups: 25, earnings: 150, current: currentTier === 2 },
    { tier: 'Tier 3', signups: 50, earnings: 350, current: currentTier === 3 },
  ];

  return (
    <div className="space-y-6">
      {/* Current Earnings Overview */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Current Earnings</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">
              ${personalStats.totalEarnings.toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">Total Earned</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">
              ${personalStats.pendingEarnings.toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">Pending Payout</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">
              ${(personalStats.totalEarnings / personalStats.totalSignups || 0).toFixed(2)}
            </div>
            <div className="text-sm text-gray-500">Per Signup</div>
          </div>
        </div>
      </div>

      {/* Tier Progress */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Tier Progress</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Current Tier: {currentTier}</span>
            <span className="text-sm text-gray-500">
              {personalStats.totalSignups} / {tier.nextTierRequirement} signups
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${tier.progress}%` }}
            />
          </div>
          {nextTier && (
            <div className="text-sm text-gray-600">
              {tier.nextTierRequirement - personalStats.totalSignups} more signups to reach Tier {nextTier}
            </div>
          )}
        </div>
      </div>

      {/* Earnings Chart */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Earnings by Tier</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={earningsData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="tier" />
            <YAxis />
            <Tooltip formatter={(value: any) => [`$${value}`, 'Earnings']} />
            <Bar 
              dataKey="earnings" 
              fill="#3B82F6"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Commission Structure */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Commission Structure</h3>
        <div className="space-y-4">
          {Object.entries(COMMISSION_RATES).map(([tierKey, tierData]: [string, CommissionTier]) => {
            if (tierKey === 'ORGANIC_BONUS') return null;
            const tierNum = tierKey.split('_')[1];
            const isCurrentTier = parseInt(tierNum) === currentTier;
            
            return (
              <div 
                key={tierKey}
                className={`p-4 rounded-lg border-2 ${
                  isCurrentTier 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      Tier {tierNum} {isCurrentTier && '(Current)'}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {tierData.signups} signups = ${tierData.bonus} bonus
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-green-600">
                      ${tierData.rate}/signup
                    </div>
                    <div className="text-sm text-gray-500">
                      after {tierData.signups} signups
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Projected Earnings */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Projected Earnings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">At Next Tier</h4>
            <div className="text-2xl font-bold text-blue-600">
              ${nextTierEarnings.toLocaleString()}
            </div>
            <p className="text-sm text-gray-600">
              If you reach Tier {nextTier} with current signups
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Potential Earnings</h4>
            <div className="text-2xl font-bold text-green-600">
              ${potentialEarnings.toLocaleString()}
            </div>
            <p className="text-sm text-gray-600">
              If you reach the next tier requirement
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 